import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { FormProvider, UseFormReturn, useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { fullFormats } from 'ajv-formats/dist/formats';
import { useDeepCompareEffect } from '@clatter/platform';

import { getUnifiedDataFromProjectValues } from '../../../helpers/getUnifiedDataFromProjectValues';
import { DynamicFormField } from './DynamicFormField';
import { FormInputWrapper } from './FormInputWrapper';
import { FormButtons } from './FormButtons';
import { StepStatus } from '../StepStatus/StepStatus';

const StyledForm = styled.form`
  & + & {
    margin-top: ${({ theme }) => theme.spacing.default};
  }
`;

const StyledFormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.large};
`;

const StyledFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.default};
`;

const StyledStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`;

const StyledStepInputs = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.large};
  background: ${({ theme }) => theme.palette.white ?? '#fff'};
  border: 1px solid ${({ theme }) => theme.palette.cardBorder ?? '#e5e7eb'};
  border-radius: ${({ theme }) => theme.radius.small ?? '8px'};
  padding: ${({ theme }) => theme.spacing.default};
`;

export interface SchemaFormRef {
  submit: () => void;
  reset: () => void;
  /** Validates the form and returns values if valid, null if invalid. Does NOT call onSubmit. */
  submitAndGetValues: () => Promise<Record<string, any> | null>;
}

interface SchemaFormProps {
  schema: JSONSchema;
  initialValues: Record<string, any>;
  isLoading?: boolean;
  query?: string;
  fieldKeyPrefix?: string;
  prefix?: string;
  hideButtons?: boolean;
  submitDisabled?: boolean;
  formMethods?: UseFormReturn<any>;
  noTargetMessage?: string | null;
  queryGqlData?: (query: string) => Promise<any>;
  onSubmit: (data: Record<string, any>) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onValidChange?: (isValid: boolean) => void;
  onSubmitButtonClick?: () => void;
}

export const SchemaForm = forwardRef<SchemaFormRef, SchemaFormProps>(
  (
    {
      schema,
      initialValues,
      isLoading = false,
      query,
      fieldKeyPrefix = '',
      queryGqlData,
      onSubmit,
      hideButtons = false,
      onDirtyChange,
      onValidChange,
      prefix = '',
      formMethods: externalFormMethods,
      submitDisabled,
      onSubmitButtonClick,
      noTargetMessage,
    },
    ref,
  ) => {
    const unifiedData = getUnifiedDataFromProjectValues({ schema, values: initialValues, query });

    const internalMethods = useForm<any>({
      mode: 'onChange',
      // @ts-ignore TS2589
      resolver: ajvResolver(unifiedData.schema as any, {
        formats: fullFormats,
        // @ts-ignore
        strict: false,
      }),
    });

    const methods = externalFormMethods ?? internalMethods;

    const {
      reset,
      handleSubmit,
      watch,
      formState: { errors, isDirty, isValid },
    } = methods;

    const formValues = watch();

    // When prefix is set (e.g. "front."), values and errors live at a nested path, not the top level.
    const scopeKeys = useMemo(
      () => (prefix ? prefix.replace(/\.$/, '').split('.') : []),
      [prefix],
    );
    const scopedValues = useMemo(
      () => (scopeKeys.length ? scopeKeys.reduce((obj: any, key) => obj?.[key], formValues) ?? {} : formValues),
      [scopeKeys, formValues],
    );
    const scopedErrors = useMemo(
      () => (scopeKeys.length ? scopeKeys.reduce((obj: any, key) => obj?.[key], errors) ?? {} : errors),
      [scopeKeys, errors],
    );

    // only manage reset internally when there are no external form methods.
    // when external methods are provided, the parent is responsible for resetting.
    const isExternalRef = useRef(!!externalFormMethods);
    useDeepCompareEffect(() => {
      if (!isExternalRef.current && unifiedData?.initialValues) {
        internalMethods.reset(unifiedData.initialValues);
      }
    }, [unifiedData.initialValues]);

    useEffect(() => {
      onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    useEffect(() => {
      onValidChange?.(isValid);
    }, [isValid, onValidChange]);

    const handleFormSubmit = (data: any) => {
      reset(data);
      onSubmit(data);
    };

    useImperativeHandle(ref, () => ({
      submit: () => handleSubmit(handleFormSubmit)(),
      reset: () => reset(),
      submitAndGetValues: () =>
        new Promise<Record<string, any> | null>((resolve) => {
          handleSubmit(
            (data) => resolve(data),
            () => resolve(null),
          )();
        }),
    }));

    const formContent = (
      <StyledForm onSubmit={handleSubmit(handleFormSubmit)}>
        <StyledFormBody>
          <StyledFieldsContainer>
            {unifiedData?.steps?.length === 0 &&
              (unifiedData?.formFields || []).map((fieldData) => (
                <FormInputWrapper
                  key={fieldKeyPrefix + fieldData.name}
                  label={fieldData.inputType === 'checkbox' ? undefined : fieldData.label}
                  required={fieldData.required}
                  helperText={fieldData.helperText}
                  errorMessage={(scopedErrors?.[fieldData.name]?.message as string) || null}
                  hidden={fieldData.inputType === 'hidden'}
                >
                  <DynamicFormField data={fieldData} prefix={prefix} disabled={isLoading} queryGqlData={queryGqlData} noTargetMessage={noTargetMessage} />
                </FormInputWrapper>
              ))}

            {(unifiedData?.steps?.length ?? 0) !== 0 &&
              (unifiedData?.steps || []).map(({ name, inputs }) => {
                const stepIsReady = inputs.every((fieldData) => {
                  if (scopedErrors?.[fieldData.name]) return false;
                  if (fieldData.required) return !!scopedValues[fieldData.name];
                  return true;
                });
                return (
                  <StyledStep key={name}>
                    <StepStatus label={name} ready={stepIsReady} large />

                    <StyledStepInputs>
                      {inputs.map((fieldData) => {
                        const errorMessage = (scopedErrors?.[fieldData.name]?.message as string) || null;
                        return (
                          <FormInputWrapper
                            key={fieldKeyPrefix + name + fieldData.name}
                            label={
                              fieldData.inputType === 'checkbox' || fieldData.inputType === 'object'
                                ? undefined
                                : fieldData.label
                            }
                            required={fieldData.required}
                            helperText={fieldData.helperText}
                            errorMessage={errorMessage}
                            hidden={fieldData.inputType === 'hidden'}
                          >
                            <DynamicFormField data={fieldData} prefix={prefix} disabled={isLoading} queryGqlData={queryGqlData} noTargetMessage={noTargetMessage} />
                          </FormInputWrapper>
                        );
                      })}
                    </StyledStepInputs>
                  </StyledStep>
                );
              })}
          </StyledFieldsContainer>

          {!hideButtons && (
            <FormButtons
              isLoading={isLoading}
              isDirty={isDirty}
              isValid={isValid}
              reset={reset}
              submitDisabled={submitDisabled}
              onSubmitClick={onSubmitButtonClick}
            />
          )}
        </StyledFormBody>
      </StyledForm>
    );

    if (externalFormMethods) return formContent;
    return <FormProvider {...internalMethods}>{formContent}</FormProvider>;
  },
);
